/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod/mini'

import {
  LooseRichTextSchema,
  type LoosenRichTextFields,
} from '../types/loose-rich-text'
import type { Equal, ExpectTrue } from '../types/validation'

import {
  ApiBackgroundSchema,
  type ApiImageBackground,
  type ApiMonochromeBackground,
} from './background'
import { type ContentLink, ContentLinkSchema } from './content-link'
import { type Image, ImageSchema } from './image'
import type { RichText } from './rich-text'

/* TypeScript */
export type CampaignSectionApiProps = {
  technicalName?: Maybe<string>
  campaignVariant?: Maybe<'hero' | 'oneUp' | 'twoUp'>
  logo?: Maybe<Image>
  title?: Maybe<RichText>
  subtitle?: Maybe<RichText>
  link?: Maybe<ContentLink>
  buttonLabel?: Maybe<string>
  buttonVariant?: Maybe<'primary' | 'secondary'>
  info?: Maybe<RichText>
  image?: Maybe<Image>
  imagePadded?: Maybe<boolean>
  contentAlignment?: Maybe<'center-left' | 'bottom-left'>
  background?: Maybe<ApiImageBackground | ApiMonochromeBackground>
  displayGradient?: Maybe<boolean>
}

export type CampaignApiProps = {
  technicalName?: Maybe<string>
  title?: Maybe<string>
  campaigns: CampaignSectionApiProps[]
}

const CampaignSectionApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  campaignVariant: z.nullish(z.enum(['hero', 'oneUp', 'twoUp'])),
  logo: z.nullish(ImageSchema),
  title: z.nullish(LooseRichTextSchema),
  subtitle: z.nullish(LooseRichTextSchema),
  link: z.nullish(ContentLinkSchema),
  buttonLabel: z.nullish(z.string()),
  buttonVariant: z.nullish(z.enum(['primary', 'secondary'])),
  info: z.nullish(LooseRichTextSchema),
  image: z.nullish(ImageSchema),
  imagePadded: z.nullish(z.boolean()),
  contentAlignment: z.nullish(z.enum(['center-left', 'bottom-left'])),
  background: z.nullish(ApiBackgroundSchema),
  displayGradient: z.nullish(z.boolean()),
})

export const CampaignApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  title: z.nullish(z.string()),
  campaigns: z.array(CampaignSectionApiPropsSchema),
})

/* Schemas validation */
type TestCampaignProps = ExpectTrue<
  Equal<
    LoosenRichTextFields<CampaignApiProps>,
    z.infer<typeof CampaignApiPropsSchema>
  >
>
